import React from 'react';
import { PageNav, PageFooter, CookieBanner } from '../components/layout/LegalLayout';

const C = { amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A', ink:'#1A1814', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF' };
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const stat = (n: string, l: string) => (
  <div style={{ textAlign:'center', padding:'24px 16px', background:C.white, borderRadius:16, border:`1px solid ${C.border}` }}>
    <p style={{ fontSize:'clamp(28px,4vw,40px)', fontWeight:800, fontFamily:font.head, color:C.amber400, letterSpacing:'-0.04em', margin:'0 0 6px' }}>{n}</p>
    <p style={{ fontSize:13, color:C.muted, fontFamily:font.body, margin:0 }}>{l}</p>
  </div>
);

const person = (name: string, role: string, bio: string) => (
  <div style={{ display:'flex', gap:18, alignItems:'flex-start', padding:'20px 0', borderBottom:`1px solid ${C.border}` }}>
    <div style={{ width:52, height:52, borderRadius:16, background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:font.head, fontWeight:800, fontSize:18, color:C.amber500, flexShrink:0 }}>
      {name.split(' ').map(w=>w[0]).join('')}
    </div>
    <div>
      <p style={{ fontSize:15, fontWeight:700, color:C.ink, fontFamily:font.body, margin:'0 0 2px' }}>{name}</p>
      <p style={{ fontSize:12, fontWeight:600, color:C.amber400, fontFamily:font.body, margin:'0 0 6px', textTransform:'uppercase', letterSpacing:'0.06em' }}>{role}</p>
      <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.65, margin:0 }}>{bio}</p>
    </div>
  </div>
);

const AboutPage: React.FC = () => (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'DM Sans', sans-serif; background: #FAFAF8; color: #1A1814; }
      #root { display: flex; flex-direction: column; min-height: 100vh; }
    `}</style>
    <PageNav/>
    <main style={{ flex:1 }}>
      {/* Hero */}
      <div style={{ background:`linear-gradient(155deg,#1A1814 0%,#2C2416 60%,#3D2E10 100%)`, padding:'80px clamp(20px,5vw,60px)', textAlign:'center' }}>
        <div style={{ maxWidth:700, margin:'0 auto' }}>
          <p style={{ fontSize:12, fontWeight:700, color:C.amber300, textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:font.body, marginBottom:16 }}>Our story</p>
          <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(32px,6vw,56px)', letterSpacing:'-0.04em', color:C.white, lineHeight:1.08, marginBottom:20 }}>
            Built for people who<br/><span style={{ color:C.amber200 }}>write to think.</span>
          </h1>
          <p style={{ fontSize:'clamp(15px,2vw,18px)', color:'rgba(255,255,255,0.55)', lineHeight:1.7, fontFamily:font.body }}>
            Jötr started because we were tired of losing notes we knew we'd written. Not deleted — just buried. We built the app we wished existed.
          </p>
        </div>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'64px clamp(20px,5vw,60px) 80px' }}>
        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:72 }}>
          {stat('1,000+', 'Notes created daily')}
          {stat('98%', 'Search accuracy')}
          {stat('<200ms', 'Average search time')}
          {stat('Free', 'Forever plan')}
        </div>

        {/* Mission */}
        <div style={{ marginBottom:64 }}>
          <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(22px,3vw,32px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:16 }}>Our mission</h2>
          <p style={{ fontSize:'clamp(15px,1.8vw,17px)', color:'#3D3A34', lineHeight:1.8 }}>
            Most note apps focus on <em>capturing</em> notes. We focus on <em>finding</em> them. Jötr is built around a single belief: a note you can't find is a note that doesn't exist.
          </p>
          <p style={{ fontSize:'clamp(15px,1.8vw,17px)', color:'#3D3A34', lineHeight:1.8, marginTop:14 }}>
            We're a small team based in Lagos, Nigeria. We think Africa deserves world-class productivity tools built locally, priced fairly, and designed for how people actually work.
          </p>
        </div>

        {/* Team */}
        <div style={{ marginBottom:64 }}>
          <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(22px,3vw,32px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:8 }}>The team</h2>
          <p style={{ fontSize:15, color:C.muted, marginBottom:24, fontFamily:font.body }}>Small team. Big obsession with search.</p>
          {person('Ilori Emmanuel',    'Co-founder & CEO',               'Visionary behind Jötr. Drives product strategy, partnerships, and the mission to build Africa\'s best note-taking experience. Believes great software starts with understanding how people actually think.')}
          {person('Akande Gbolahan',  'Lead Backend Engineer',           'Architects the infrastructure that powers Jötr — from the PostgreSQL full-text search engine to the cloud sync system. Obsessed with performance, reliability, and clean APIs.')}
          {person('Abejide Ezekiel',  'Head of Social Media & Community','Shapes Jötr\'s voice and grows our community across X, Instagram, and LinkedIn. Turns users into advocates and feedback into product insights.')}
        </div>

        {/* Values */}
        <div style={{ background:C.amber100, border:`1px solid ${C.amber200}`, borderRadius:20, padding:'36px 40px' }}>
          <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(20px,2.5vw,28px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:24 }}>What we believe</h2>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:20 }}>
            {[
              ['Search first',     'Everything we build starts with: can you find it later?'],
              ['Privacy by default','Your notes are yours. We never read, sell, or train on them.'],
              ['Free and fair',    'A powerful free plan. No dark patterns. No surprise charges.'],
              ['Built in Africa',  "Proudly Nigerian. Designed for the world's fastest-growing internet population."],
            ].map(([h,d]) => (
              <div key={h}>
                <p style={{ fontSize:14, fontWeight:700, color:C.amber500, fontFamily:font.head, marginBottom:6 }}>{h}</p>
                <p style={{ fontSize:13, color:'#3D3A34', fontFamily:font.body, lineHeight:1.65, margin:0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
    <PageFooter/>
    <CookieBanner/>
  </>
);

export default AboutPage;